import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/apiService';
import { workspaceService } from '../services/workspaceService';
import { collectionService } from '../services/collectionService';
import teamsService from '../services/teamsService';
import {
  connectSocket,
  disconnectSocket,
  addPlanUpdateListener,
  addSessionReplacedListener,
  addSessionEndedListener,
  addUserRejoinListener,
} from '../services/channelService';
import { useNotificationStore } from './notificationStore';
import { syncLinkedAccountsFromIdp } from '../services/idpAccountService';
import { refreshEntitlements } from '../plans/entitlementsFeed';
import { planUpdatePatch } from '../utils/planCancellation';
import * as oidc from '../services/oidcService';
import { persistTokens, clearTokens, getAccessToken, getRefreshToken, startProactiveRefresh } from '../services/tokenRefresher';
import { clearSyncAuth } from '../services/syncAuthBridge';
import {
  setStorageNamespace,
  clearAccountNamespace,
  getStorageNamespace,
  LOCAL_NAMESPACE,
} from '../services/storageNamespace';
import { purgeAccountData } from '../services/accountData';
import { endWebSession } from '../services/webSession';
import { isWebApp } from '../platform/target';
import {
  classifyAuthInitFailure,
  clearsSession,
  AUTH_INIT_OFFLINE,
} from './authInitFailure';
import { runSessionEffects } from './sessionEffects';
import {
  startAuthReconnect,
  stopAuthReconnect,
  AUTH_RECONNECT_SUCCESS,
  AUTH_RECONNECT_STOP,
  AUTH_RECONNECT_RETRY,
} from '../services/authReconnect';

let planListenerRegistered = false;

/**
 * The account a teardown is LEAVING — the id the account-root purge is named
 * with, so it can never degrade into "delete whichever account is open".
 *
 * `user` is USUALLY enough: zustand's persist rehydrates it synchronously at
 * store creation, so a signed-in cold start knows the id before the first
 * render. It is not always enough. `initializeAuth` flips `isAuthenticated`
 * to true off the stored token alone, and Sign out is a plain title-bar
 * action clickable the whole time, so `user == null && isAuthenticated` is
 * reachable whenever the persisted `auth-storage` blob is missing or
 * truncated while `authToken` survives, and just after the 401 interceptor's
 * `auth:logout` handler nulls `user`. The storage namespace is resolved from
 * the same persisted session and is the fallback for exactly those cases.
 *
 * Returns null only when there genuinely is no signed-in account to name, and
 * a null id must purge NOTHING — see purgeAccountData / sync::purge_target_id.
 */
export function outgoingAccountId(user) {
  const fromStore = user?.id;
  if (fromStore != null && String(fromStore) !== '') return String(fromStore);
  try {
    const ns = getStorageNamespace();
    return ns == null || ns === LOCAL_NAMESPACE ? null : String(ns);
  } catch {
    return null;
  }
}

// Backend validation errors always carry a generic top-level `message`
// ("Validation error") with the real reason in `errors: { field: [msg] }`.
// Prefer the field-specific detail when present.
function extractErrorMessage(error, fallback) {
  const data = error.response?.data;
  if (data?.errors) {
    const detail = Object.values(data.errors).flat().join(' ');
    if (detail) return detail;
  }
  return data?.message || fallback;
}

function registerPlanListener(set) {
  if (planListenerRegistered) return;
  planListenerRegistered = true;
  addPlanUpdateListener((payload) => {
    // `cancel_at` (backend #223) rides the payload; planUpdatePatch keeps a
    // resume from leaving a stale end date behind (utils/planCancellation.js).
    set((s) => ({ user: { ...s.user, ...planUpdatePatch(payload) } }));
    // Plans v2: a plan change changes what may be created locally.
    refreshEntitlements(useAuthStore.getState().user, authService.getUsage, authService.getPlanClaim);
  });
  // COMMS-07: plan_updated pushes ride the same gapless user:<id> channel as
  // chat/notifications — a reconnect after any blip can silently drop a plan
  // change (e.g. a webhook-driven upgrade/downgrade) for the rest of the
  // session, with nothing to correct it short of a full app restart. Refetch
  // the current user/plan on rejoin so the plan banner can't go stale forever.
  addUserRejoinListener(() => {
    authService.getUser()
      .then((user) => set((s) => ({ user: { ...s.user, ...user } })))
      .catch(() => {});
  });
  // Session cap (2 devices): a login elsewhere evicted the oldest sessions.
  // The IdP names the revoked sessions by access-token `jti` — only the device
  // holding a revoked token signs out. (`revoked_token_ids` is the legacy
  // Sanctum shape, kept for the AUTH_JWT_ENABLED=false rollback path.)
  addSessionReplacedListener(async (payload) => {
    const ownToken = getAccessToken() || '';
    let evicted = false;

    if (Array.isArray(payload?.revoked_jtis)) {
      const ownJti = oidc.peekJti(ownToken);
      evicted = Boolean(ownJti) && payload.revoked_jtis.map(String).includes(String(ownJti));
    } else if (Array.isArray(payload?.revoked_token_ids)) {
      const ownId = String(ownToken).split('|')[0];
      evicted = Boolean(ownId) && payload.revoked_token_ids.map(String).includes(ownId);
    }

    if (!evicted) return;
    // This session is already revoked server-side. Drop tokens before logout
    // so the logout call's own 401 (it will get one — the session is already
    // gone server-side) can't race the teardown this function is about to
    // run itself.
    clearTokens();
    await useAuthStore.getState().logout();
    useNotificationStore
      .getState()
      .info('You were signed out because this account was signed in on too many devices.', 'Signed out');
  });
}

/**
 * THE post-sign-in wiring list, in one place. Both the cold-start happy path
 * and the offline-recovery retry run exactly this — do not fork the list, or
 * a recovered session gets a different (weaker) app than a normal one. The
 * session-cap forced-logout control rides `registerPlanListener`, so a path
 * that skips any of these is a security regression, not just a UX one.
 */
function wireSession(set, user) {
  runSessionEffects([
    ['connectSocket', () => connectSocket(getAccessToken(), user.id)],
    ['registerPlanListener', () => registerPlanListener(set)],
    ['syncLinkedAccountsFromIdp', () => syncLinkedAccountsFromIdp()],
    // Plans v2: what this plan may create on the machine (card 3.1).
    ['refreshEntitlements', () => refreshEntitlements(user, authService.getUsage, authService.getPlanClaim)],
  ]);
}

/**
 * One offline-recovery try, reported in authReconnect's outcome vocabulary.
 * Mirrors initializeAuth's own getUser handling: only positive evidence of a
 * dead session (a real auth rejection) may clear tokens; anything else keeps
 * the session and asks the loop to try again later.
 */
function attemptSessionRecovery(set) {
  // Defensive: the session-ending paths stop the loop, but an attempt already
  // in flight when logout ran must not fetch/wire against a dead session.
  if (!getAccessToken()) return Promise.resolve(AUTH_RECONNECT_STOP);
  return authService.getUser().then(
    (user) => {
      setStorageNamespace(user?.id);
      set({ user, isOffline: false });
      wireSession(set, user);
      return AUTH_RECONNECT_SUCCESS;
    },
    (error) => {
      const outcome = classifyAuthInitFailure(error);
      if (clearsSession(outcome)) {
        // Same teardown as initializeAuth's catch: the server positively
        // rejected the credentials during a retry, so the session is over.
        clearTokens();
        setStorageNamespace(null);
        set({ token: null, isAuthenticated: false, user: null, isOffline: false });
        return AUTH_RECONNECT_STOP;
      }
      return AUTH_RECONNECT_RETRY;
    }
  );
}

// disconnectSocket() clears channelService's planHandlers/sessionReplacedHandlers
// Sets on EVERY session end (hard, via authStore.logout(), or soft — e.g.
// apiClient.js's 401 interceptor, which never calls authStore.logout() at
// all). Without this, planListenerRegistered stayed stuck `true` after a
// soft logout (the only other reset site was inside logout() itself, which
// that path never reaches), so registerPlanListener() no-op'd on the next
// login and never re-attached to the freshly built socket/channel —
// plan-change realtime and the multi-device session-cap forced-logout
// mechanism went silently dead for the rest of the app session. Registered
// once, unconditionally, at module load so it fires for every session end
// regardless of which path triggered it — this is the "textbook" version of
// the pattern addSessionEndedListener's invariant doc describes: no other
// module needs to know this flag exists.
addSessionEndedListener(() => {
  planListenerRegistered = false;
});

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isInitialized: false,
      // True when the cold-start /user call could not reach the API. The
      // session is intact — this is "we could not check", never "you are
      // signed out". Deliberately NOT persisted: it describes this instant's
      // connectivity, and a stale `true` restored from disk would claim the
      // app is offline before it has tried anything.
      isOffline: false,

      // The ONLY sign-in path: OIDC Authorization Code + PKCE through the
      // system browser at auth.apiboo.com (email/password, registration,
      // password reset, 2FA, passkeys and GitHub all live on the hosted
      // pages), back via the Rust loopback listener.
      loginWithBrowser: async () => {
        set({ isLoading: true, error: null });
        try {
          const tokens = await oidc.login();
          // A fresh sign-in supersedes any cold-start recovery loop — this
          // path wires everything itself, and a stale loop firing later must
          // not wire (or 401-teardown) the NEW session.
          stopAuthReconnect();
          persistTokens(tokens);

          const user = await authService.getUser();
          // Point every account-owned storage key at THIS user before any
          // store writes land — from here on, no key name this session can
          // build reaches another account's data.
          setStorageNamespace(user?.id);
          set({
            user,
            token: tokens.accessToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            // A reachable API is a precondition of getting here, so whatever
            // a failed cold start concluded is now out of date.
            isOffline: false,
          });
          connectSocket(tokens.accessToken, user.id);
          registerPlanListener(set);
          // Mirror the IdP-owned linked accounts (e.g. GitHub, when signed in
          // with it) so Settings shows "Connected" and git-sync has the token.
          syncLinkedAccountsFromIdp();
          refreshEntitlements(user, authService.getUsage, authService.getPlanClaim);
          return { success: true };
        } catch (error) {
          set({ isLoading: false, error: null });
          const msg = error?.message || String(error || '');
          // The loopback listener rejects on cancel (listener released) or timeout.
          if (msg.includes('No OAuth callback listener') || /cancel/i.test(msg)) {
            return { success: false, cancelled: true };
          }
          if (/timed out/i.test(msg)) {
            return { success: false, error: 'Sign-in timed out. Please try again.' };
          }
          const data = error.response?.data;
          return { success: false, error: data?.message || msg || 'Sign-in failed' };
        }
      },

      // Cancel an in-progress browser login (releases the loopback listener).
      cancelLoginWithBrowser: async () => {
        await oidc.cancel();
      },

      // `purge` deletes this account's on-disk root (everything but the Vault).
      // ONLY a sign-out the user asked for may do that — the Sign out / Switch
      // account / Delete account buttons pass `{ purge: true }`. Every other
      // session end (server-forced sign-out, a failed session check, the soft
      // `auth:logout` path) keeps the data and just asks to sign in again: an
      // unreachable or misconfigured API wiped MCP tokens and grants on
      // 15.09.2026, and "on this device" items exist nowhere else (MCP board
      // card "A sign-out nobody clicked wipes the account folder").
      //
      // `keepLocalOnly` (24.09.2026): the sign-out dialog's "Keep on this
      // device" answer — see signOutStore. Only meaningful with `purge: true`.
      // `keepMirrorIds` (04.10.2026): with it, the workspaces whose git history
      // exists only on this device keep their mirror folder (and history).
      // `keepScopeMirrors` (T10): the same for "My projects, boards & documents"
      // tracked on this device only — its `scope-mirror/<folder>`.
      logout: async ({ purge = false, reason = 'session_ended', keepLocalOnly = false, keepMirrorIds = [], keepScopeMirrors = [], removeLocalHistories = false } = {}) => {
        // Capture before any state reset — the purge below needs to name the
        // OUTGOING account, not whatever comes next. Resolved through
        // outgoingAccountId() rather than read straight off `user`, which is
        // null for the whole cold-start window.
        const outgoingUserId = outgoingAccountId(get().user);
        try {
          await authService.logout();
        } catch (error) {
          console.error('Logout error:', error);
        }
        // Best-effort: kill the session at the IdP too.
        try {
          await oidc.revoke(getRefreshToken());
        } catch {
          /* revocation is best-effort */
        }

        // disconnectSocket() is the single trigger point for the universal
        // session-ended signal (see channelService.js's
        // addSessionEndedListener) — every module that mirrors
        // session-derived state (the AI socket, plan-listener registration,
        // linked accounts, sync-status banners, chat/team/workspace channel
        // state, ...) resets ITSELF off of that, so this function does not
        // enumerate them. Calling it directly here (rather than relying
        // solely on the `auth:logout` dispatch below reaching a mounted
        // useWindowChrome listener) is deliberate defense in depth.
        disconnectSocket();
        // The offline-recovery loop belongs to the session that just ended.
        // (attemptSessionRecovery also self-stops on missing tokens, but the
        // loop must not keep waking up against a signed-out app.)
        stopAuthReconnect();
        clearTokens();

        // Delete the outgoing account's on-disk root
        // (<AppLocalData>/users/<id>/): request/response bodies with their auth
        // headers, YAML workspace mirrors, and every Rust-written artifact.
        // Local workspace documents live outside it, in
        // `local-workspaces/ns-<id>/`, and survive. It is NOT all re-derivable
        // any more: boards/documents "On this device" and changes not yet synced
        // live in its database — which is why Sign out goes through
        // signOutStore.requestSignOut, and `keepLocalOnly` keeps exactly those
        // (sync/signout.rs).
        //
        // The VAULT also survives, since 19.08.2026: it is not re-derivable on
        // a plan that never syncs, and this path used to destroy it. Only its
        // contents are encrypted, though — the rest of `apiboo.db` is
        // plaintext unless the user opted into encryption at rest — which is
        // why the purge still empties everything else.
        //
        // Never called with a null id: `purgeAccountData` would refuse anyway,
        // but the point is that "unknown account" must not reach a delete at
        // all — the id is resolved above, and a genuinely-absent one means
        // there is no account root to purge.
        if (purge === true && outgoingUserId != null) {
          await purgeAccountData(outgoingUserId, reason, { keepLocalOnly: keepLocalOnly === true, keepMirrorIds, keepScopeMirrors, removeLocalHistories: removeLocalHistories === true });
        }

        // Drop every account-owned browser key in one move and switch back to
        // the signed-out `_local` namespace. This replaces the hand-maintained
        // removeItem() list that used to live here: that list had drifted into
        // naming keys nothing ever wrote ('workspaces', 'collections',
        // 'history', ...) while the real `api-client-*` family it was meant to
        // cover survived every logout. A list that must be updated for each
        // new key WILL drift; namespaced keys leave nothing to enumerate.
        // `auth-storage` is not removed either — the set() below immediately
        // re-persists it in its signed-out shape.
        clearAccountNamespace();

        // Clear service caches
        workspaceService.clearCache();
        collectionService.clearCache();
        teamsService.clearCache();

        // Reset auth state (stay initialized so guest workspace load runs without a reload)
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
          isInitialized: true,
          isOffline: false,
        });

        // Dispatch custom event for components to clear their state
        window.dispatchEvent(new CustomEvent('auth:logout'));
      },

      // Name-only — email and password changes live on the IdP's hosted
      // account page (oidc.accountUrl()).
      updateProfile: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const updatedUser = await authService.updateProfile(userData);
          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          });
          return { success: true };
        } catch (error) {
          const errorMessage = extractErrorMessage(error, 'Profile update failed');
          set({ isLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      clearError: () => set({ error: null }),

      fetchUsage: async () => {
        try {
          const usage = await authService.getUsage();
          set((s) => ({ user: { ...s.user, _usage: usage } }));
          return usage;
        } catch {
          return null;
        }
      },

      cancelPlan: async () => {
        try {
          const updated = await authService.cancelPlan();
          set((s) => ({ user: { ...s.user, ...updated } }));
          return { success: true };
        } catch (error) {
          const errorMessage = extractErrorMessage(error, 'Failed to cancel plan');
          return { success: false, error: errorMessage };
        }
      },

      reactivatePlan: async () => {
        try {
          const updated = await authService.reactivatePlan();
          set((s) => ({ user: { ...s.user, ...updated } }));
          return { success: true };
        } catch (error) {
          const errorMessage = extractErrorMessage(error, 'Failed to reactivate plan');
          return { success: false, error: errorMessage };
        }
      },

      // Initialize auth state from localStorage
      initializeAuth: () => {
        const { isInitialized } = get();
        if (isInitialized) {
          return; // Prevent duplicate initialization
        }

        const token = getAccessToken();
        if (token) {
          // The persisted user (zustand `auth-storage`) rehydrates
          // synchronously, so the account namespace is already correct on the
          // first render — before getUser() resolves — and no read can land in
          // `_local`, or worse in whatever namespace was last active.
          setStorageNamespace(get().user?.id);
          set({ token, isAuthenticated: true, isInitialized: true });
          // Plans v2: the persisted user's plan and its cached signed claim
          // (plan_claim_load works offline) apply NOW, not after getUser():
          // until then the entitlements store says Free, and a paying user
          // starting offline (or before the API answers) was refused creates.
          // No network here; wireSession's full refresh supersedes it.
          const persistedUser = get().user;
          if (persistedUser) refreshEntitlements(persistedUser);
          // Keep the stored token fresh even when no HTTP traffic runs — the
          // Phoenix socket presents it on every reconnect handshake.
          startProactiveRefresh();
          // The interceptors transparently refresh an expired access token
          // (and log out when the refresh token is gone/revoked).
          //
          // The shape of this chain is the fix, not decoration. Only the
          // getUser() call and the state write it produces sit upstream of
          // the catch; the post-sign-in wiring is a SEPARATE link placed
          // after it, so a throw from connectSocket / registerPlanListener /
          // syncLinkedAccountsFromIdp can never be handled as "this session
          // is invalid". Do not collapse these back into one .then().
          authService.getUser()
            .then((user) => {
              setStorageNamespace(user?.id);
              set({ user, isOffline: false });
              return user;
            })
            .catch((error) => {
              const outcome = classifyAuthInitFailure(error);
              if (clearsSession(outcome)) {
                clearTokens();
                setStorageNamespace(null);
                set({ token: null, isAuthenticated: false, user: null, isOffline: false });
                // A dead session here (e.g. `device_revoked`, which skips the
                // interceptor's logout) must also take Rust off the account's
                // database, or the MCP server keeps serving it behind the
                // sign-in page.
                clearSyncAuth();
                // The persisted user's plan applied above belongs to a dead session.
                refreshEntitlements(null);
              } else {
                // Keep the session. The stored refresh token is still good;
                // the app simply could not reach the API right now. The
                // persisted user (rehydrated synchronously above) carries the
                // UI until the next successful call.
                set({ isOffline: true });
                // This is the ONLY place that starts the recovery loop,
                // because this is the only place that produces the state it
                // recovers from: a kept session with none of the wiring run.
                // Without it, "reconnect on its own" below was a false
                // promise — initializeAuth is called once per launch and no
                // 'online' listener existed for auth — and the session-cap
                // forced-logout listener stayed off for the whole app run.
                startAuthReconnect(() => attemptSessionRecovery(set));
                useNotificationStore
                  .getState()
                  .warning(
                    outcome === AUTH_INIT_OFFLINE
                      ? 'Could not reach Apiboo. You are still signed in — this will reconnect on its own.'
                      : 'Apiboo is not responding right now. You are still signed in.',
                    'Working offline'
                  );
              }
              // null means "no wiring": the effects link below must not run
              // against a session that failed to load.
              return null;
            })
            .then((user) => {
              if (!user) return;
              wireSession(set, user);
            })
            // Backstop only. runSessionEffects is total by construction, so
            // nothing should arrive here — but if it ever does, the one thing
            // this must not do is touch tokens. Never "simplify" this into
            // the catch above.
            .catch((error) => {
              console.warn('[auth] session wiring failed:', error?.message || error);
            });
        } else {
          // No session: account keys belong to the signed-out `_local` space.
          // `isAuthenticated` is persisted: a stale `true` without a token
          // (storage partly lost) must not open the app. And Rust may still
          // have the last account's database open from device.json — signed
          // out means off it (no MCP serving behind the sign-in page).
          setStorageNamespace(null);
          set({ isInitialized: true, isAuthenticated: false, token: null, user: null });
          clearSyncAuth();
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // token is intentionally NOT persisted here — it is stored and managed
        // exclusively via tokenRefresher's explicit localStorage handling.
        // Persisting it in Zustand would create a redundant copy that
        // is an extra attack surface.
        // isInitialized is intentionally NOT persisted — it must start false on
        // every page load so initializeAuth always re-fetches the user from the
        // server (picks up plan changes, admin changes, etc.).
      }),
    }
  )
);

// apiClient.js's 401 interceptor drops tokens and fires this event without
// going through logout() (it can't safely await IdP revocation / clear other
// stores from inside an axios interceptor). Without this listener,
// isAuthenticated stayed stuck true after the tokens were gone — the UI kept
// rendering chat/team features as live while every request silently 401'd,
// instead of showing a sign-in prompt. isInitialized stays true so guest/local
// workspace rendering doesn't re-block on the auth init gate.
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    // Same teardown as logout(), so the soft path (401 interceptor, which
    // never calls logout()) can't leave an account's storage behind for the
    // next sign-in. Both are no-ops when logout() already ran — the namespace
    // is `_local` and the account root is gone by then.
    // NO purge here: this is the soft path (a 401 interceptor, a forced
    // sign-out) — nobody asked to sign out, so the account's data stays on
    // disk (it is per-account, so the next account cannot see it). Only an
    // explicit sign-out purges — see logout({ purge }).
    clearAccountNamespace();
    // Web app: the refresh token is the server's cookie, and no amount of
    // local clearing touches it. Without this the next request refreshes the
    // user straight back in off a session they were just signed out of.
    if (isWebApp()) {
      endWebSession().catch(() => {});
    }
    // The soft path never runs logout(), so it must stop the recovery loop
    // itself too — same reasoning as the purge/namespace lines above.
    stopAuthReconnect();
    useAuthStore.setState({ user: null, token: null, isAuthenticated: false, isInitialized: true });
  });
}

export const useIsAdmin = () => useAuthStore((s) => s.user?.is_admin === true);
export const useUserPlan = () => useAuthStore((s) => s.user?.plan ?? 'free');
